<template>
<div id="app-sidebar">
  <a href="#main" class="sr-only">Jump to content</a>
  <nav class="minimal-nav">
    <ol>
      <li><button :class="{'is-open': sidebarOpen}" @click="toggleSidebar"><span class="sr-only">Open navigation</span></button></li>
      <li v-for="(item, index) in items" :key="index" :class="{'is-active': index == active, 'is-checked': index < active}">
        <router-link v-if="index < active" :to="item.path">{{ index + 1 }}</router-link>
        <span v-else>{{ index + 1 }}</span>
      </li>
    </ol>
  </nav>
  <div class="sidebar">
    <div class="body">
      <router-link to="/"><img class="logo" src="/assets/logo.svg" width="180" height="37"></router-link>
      <div class="input-group" v-show="editingName">
        <label for="update-name" class="sr-only">Update mix name</label>
        <input ref="newMixName" id="update-name" type="text" class="form-control inline" @keyup.enter="saveName" @keyup.escape="cancelEditName" v-model.trim="$v.newMixName.$model">
        <button @click="saveName" :disabled="!$v.newMixName.required">Save</button>
        <button class="cancel" @click="cancelEditName">Cancel</button>
      </div>
      <div class="title-group" v-show="!editingName">
        <button class="heading-secondary" ref="actionsToggle" @click="toggleDropdown" :class="{'has-dropdown': active > 0, 'is-open': showItems}">{{ mixName }}</button>
        <div class="dropdown" v-closable="{ exclude: ['actionsToggle'], handler: 'handleClose' }">
          <ul class="items" v-if="showItems">
            <li><a href="#" @click.prevent="editName" class="edit">Edit mix name</a></li>
            <li><a href="#" @click.prevent="saveProgress" class="save">Finish later</a></li>
            <li><a href="#" @click.prevent="showResetWarning = true" class="reset">Start over</a></li>
            <li class="confirmation" v-if="showResetWarning">Are you sure? <a href="/start">Yes, start over</a></li>
          </ul>
        </div>
      </div>
      <div class="progress-bar" v-show="!editingName"><span class="progress" :style="progressWidth"></span></div>
      <nav class="main-nav">
        <ol>
          <li v-for="(item, index) in items" :key="index" :class="{'is-active': index == active, 'is-checked': item.valid}">
            <router-link :to="item.path">{{ item.text }}</router-link>
          </li>
        </ol>
      </nav>
    </div>
    <div class="footer">
      <router-link to="contact"><img src="/assets/mail.svg" alt=""> Contact Us</router-link>
    </div>
    <div class="sidebar-overlay" @click="toggleSidebar"></div>
  </div>
</div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
export default {
  props: ['active'],
  data () {
    return {
      sidebarOpen: false,
      showItems: false,
      editingName: false,
      originalMixName: '',
      defaultMixName: 'Your new mix',
      showResetWarning: false
    }
  },
  validations: {
    newMixName: { required }
  },
  methods: {
    toggleSidebar () {
      if (this.sidebarOpen) {
        this.$emit('closeSidebar')
        this.sidebarOpen = false
      } else {
        this.$emit('openSidebar')
        this.sidebarOpen = true
      }
    },
    editName () {
      this.editingName = true
      this.showItems = false
      if (this.$store.state.formData.mixName) {
        this.originalMixName = this.$store.state.formData.mixName
      } else {
        this.originalMixName = this.defaultMixName
      }
      this.$nextTick(() => this.$refs.newMixName.focus())
    },
    saveName () {
      this.editingName = false
    },
    cancelEditName () {
      this.$store.state.formData.mixName = this.originalMixName
      this.editingName = false
    },
    saveProgress () {
      this.showItems = false
      this.$emit('saveProgress')
    },
    toggleDropdown () {
      this.showItems = !this.showItems
      if (!this.showItems) {
        this.showResetWarning = false
      }
    },
    handleClose () {
      this.showItems = false
    }
  },
  computed: {
    newMixName: {
      get () {
        return this.$store.state.formData.mixName
      },
      set (value) {
        this.$store.commit('updateMixName', value)
      }
    },
    mixName () {
      let name = this.$store.state.formData.mixName
      return name === '' ? this.defaultMixName : name
    },
    progressWidth () {
      return 'width: ' + this.active / 5 * 100 + '%'
    },
    items () {
      let startValid = false
      if (this.$store.state.formData.mixName &&
        this.$store.state.formData.packagingOption &&
        this.active >= 1) {
        startValid = true
      }
      return [
        { text: 'Getting Started', path: '/start', valid: startValid },
        { text: 'Planting Time', path: '/time', valid: this.active >= 2 && this.$store.state.formData.plantingTime !== '' },
        { text: 'Goals', path: '/goals', valid: this.active >= 3 },
        { text: 'Species', path: '/species', valid: this.active >= 4 && this.$store.state.formData.selectedCrops.length },
        { text: 'Inoculation', path: '/inoculation', valid: this.active >= 5 },
        { text: 'Summary', path: '/summary', valid: false } // never a time when this will be checked
      ]
    }
  }
}
</script>

<style lang="scss">
  .sidebar-overlay{
    position: fixed;
    z-index: $zi-ui;
    top: $min-nav-height;
    left: 0;
    right: 0;
    height: 100%;
    background: rgba($color-green, 0.75);
    opacity: 0;
    transition: all $ui-transition ease;
    pointer-events: none;
    @include media-breakpoint-up(lg){
      display: none;
    }
  }
  .sidebar {
    position: fixed;
    left: -$sidebar-width-xs;
    top: 0;
    width: $sidebar-width-xs;
    height: 100vh;
    display: flex;
    flex-direction: column;
    text-align: center;
    color: $color-white;
    transition: all $ui-transition ease;
    z-index: $zi-ui;
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
    @include media-breakpoint-up(lg){
      width: $sidebar-width;
      left: -$sidebar-width;
      &::after{
        display: none;
      }
    }
    .logo {
      margin: 0 0 78px;
    }
    .main-nav {
      margin-top: 40px;
    }
    .body{
      background: $sidebar-bg;
      flex: 1;
      padding-top: 40px;
      .input-group{
        margin: 0 30px;
        text-align: left;
        &.is-valid{
          &::after{
            display: none;
          }
        }
        .form-control{
          font-size: 30px;
          padding: 0 55px 0 0;
          &::-webkit-input-placeholder { color: #fff; opacity: .5;  }
          &::-moz-placeholder { color: #fff; opacity: .5;  }
          &:-ms-input-placeholder { color: #fff; opacity: .5;  }
          &:-moz-placeholder { color: #fff; opacity: .5;  }
        }
        .error{
          top: -20px;
        }
        button{
          padding: 2px 6px 1px;
          border: 0;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 600;
          font-size: 14px;
          background: $color-neon-green;
          color: $color-dark-green;
          margin: 5px 5px 0;
          border-radius: 3px;
          &[disabled]{
            opacity: .5;
          }
          &.cancel{
            background: none;
            color: $color-neon-green;
          }
        }
      }
    }
    .footer{
      margin-top: 1px;
      background: $sidebar-bg;
      a{
        color: $color-white;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 1px;
        font-size: 14px;
        transition: .2s;
        &:hover{
          text-decoration: none;
          background: rgba($color-dark-green, 0.8);
        }
        img{
          margin-right: 10px;
        }
      }
    }
  }
  .title-group{
    position: relative;
    .dropdown{
      .items{
        width: 288px;
        left: 50%;
        transform: translateX(-50%);
        top: 0;
        li{
          padding-left: 32px;
        }
        a{
          padding-left: 28px;
          background-repeat: no-repeat;
          background-position: left center;
          &.edit{
            background-image: url('/assets/pencil.svg');
          }
          &.save{
            background-image: url('/assets/link.svg');
          }
          &.reset{
            background-image: url('/assets/reset.svg');
          }
        }
      }
    }
  }
  .main-nav {
    text-align: left;

    ol {
      list-style: none;
      counter-reset: custom-counter;
      margin: 0;
      padding: 0 0 0 30px;

      li {
        counter-increment: custom-counter;
        display: block;
        font-size: 16px;
        font-weight: 600;
        color: $color-white;
        padding: 12px 0 12px 32px;
        border-radius: 3px 0 0 3px;
        position: relative;
        &::before {
          content: counter(custom-counter);
          font-size: 24px;
          font-weight: 400;
          color: rgba($color-white, 0.35);
          width: 20px;
          text-align: center;
          display: inline-block;
          margin-right: 12px;
          vertical-align: middle;
        }
        &.is-active {
          background: $navbar-active-bg;
          border-right: 6px solid $color-neon-green;
          &::before {
            color: rgba($color-white, 1.0);
          }
          a{
            pointer-events: none;
          }
        }
        &.is-checked {
          &::after{
            content: '';
            background: url('/assets/checkmark.svg') no-repeat center center;
            width: 12px;
            height: 12px;
            position: absolute;
            top: 16px;
            left: 40px;
          }
        }
      }
    }
    a{
      color: #fff;
      &:hover{
        color: #fff;
        text-decoration: none;
      }
    }
  }
  .heading-secondary {
    @include resetButton;
    font-size: 30px;
    margin: 0 0 10px;
    color: $color-white;
    font-weight: 400;
    padding: 0 30px;
    line-height: 46px;
    &:focus{
      outline: none;
    }
    &.has-dropdown{
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      &.is-open {
        &::after {
          transform: rotate(-180deg);
        }
      }
      &::after{
        content: '';
        width: 12px;
        height: 17px;
        margin-left: 6px;
        background: url('/assets/dropdown-arrow.svg') no-repeat center center;
        display: block;
        transition: all 0.2s ease-in-out;
      }
    }
  }
  .minimal-nav{
    position: fixed;
    z-index: $zi-ui;
    top: -$min-nav-height;
    width: 100vw;
    left: 0;
    background: $color-dark-green;
    transition: all $ui-transition ease;
    text-align: center;
    @include media-breakpoint-up(lg){
      display: none;
    }
    ol{
      display: flex;
      list-style: none;
      padding: 0;
      margin: 0;
      li{
        flex: 1;
        height: $min-nav-height;
        border-bottom: solid 6px $color-dark-green;
        position: relative;
        span, a{
          display: block;
          width: 100%;
          height: 100%;
          font-size: 24px;
          line-height: $min-nav-height;
          color: $color-white;
          opacity: .35;
        }
        &.is-active{
          border-bottom-color: $color-neon-green;
          span, a{
            opacity: 1;
          }
        }
        &.is-checked{
          &::after{
            content: '';
            background: url('/assets/checkmark-dark.svg') no-repeat center center;
            width: 12px;
            height: 12px;
            position: absolute;
            top: 16px;
            left: 50%;
          }
        }
      }
    }
    button{
      background: none;
      padding: 0;
      border: 0;
      border-right: solid 1px $color-green;
      width: 100%;
      height: $min-nav-height;
      background: url('/assets/menu.svg') center center no-repeat;
      &:focus{
        outline: none;
      }
      &.is-open{
        background-image: url('/assets/close.svg')
      }
    }
  }
</style>
